import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Gift, Wallet, CreditCard, Ticket, Star, Bell, Heart, MapPin, User as UserIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { toast } from "sonner";

export const Route = createFileRoute("/account/$section")({
  component: AccountSectionPage,
});

const META: Record<string, { title: string; icon: React.ReactNode; empty: string }> = {
  addresses: { title: "Manage Addresses", icon: <MapPin className="h-10 w-10" />, empty: "No saved addresses yet. Addresses are collected at checkout." },
  "gift-cards": { title: "Gift Cards", icon: <Gift className="h-10 w-10" />, empty: "You have no gift cards. Promotions will appear here." },
  upi: { title: "Saved UPI", icon: <Wallet className="h-10 w-10" />, empty: "No UPI IDs saved yet." },
  cards: { title: "Saved Cards", icon: <CreditCard className="h-10 w-10" />, empty: "No saved cards yet." },
  coupons: { title: "My Coupons", icon: <Ticket className="h-10 w-10" />, empty: "No coupons available right now. Check back soon!" },
  reviews: { title: "My Reviews & Ratings", icon: <Star className="h-10 w-10" />, empty: "You haven't rated any product yet." },
  notifications: { title: "All Notifications", icon: <Bell className="h-10 w-10" />, empty: "You're all caught up. No new notifications." },
  wishlist: { title: "My Wishlist", icon: <Heart className="h-10 w-10" />, empty: "Your wishlist is empty. Tap the heart on a product to save it." },
};

function AccountSectionPage() {
  const { section } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth" });
  }, [loading, user, navigate]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <SiteHeader />
      <div className="mx-auto w-full max-w-3xl px-4 py-6 flex-1">
        <Link to="/account" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to Account
        </Link>

        {section === "profile" ? (
          <ProfileSection />
        ) : (
          <PlaceholderSection sectionKey={section} />
        )}
      </div>
      <SiteFooter />
    </div>
  );
}

function ProfileSection() {
  const { user } = useAuth();
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle();
      setFullName(data?.full_name ?? (user.user_metadata?.full_name as string) ?? "");
    })();
  }, [user]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("profiles").update({ full_name: fullName }).eq("id", user.id);
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Profile updated");
  };

  const changeEmail = async () => {
    const trimmed = newEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (trimmed === user?.email) {
      toast.error("That is already your current email");
      return;
    }
    setEmailSaving(true);
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setEmailSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Confirmation link sent. Check both your old and new inbox to confirm the change.");
    setEditEmail(false);
    setNewEmail("");
  };

  return (
    <section className="bg-card rounded-md border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <UserIcon className="h-6 w-6 text-primary" />
        <h1 className="text-lg font-semibold">Profile Information</h1>
      </div>

      <div className="space-y-4 max-w-md">
        <div>
          <label className="text-xs text-muted-foreground">Full Name</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
            placeholder="Your name"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground">Email</label>
            {!editEmail && (
              <button
                type="button"
                onClick={() => { setEditEmail(true); setNewEmail(user?.email ?? ""); }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Change
              </button>
            )}
          </div>
          {editEmail ? (
            <div className="mt-1 space-y-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                placeholder="new@email.com"
              />
              <p className="text-[11px] text-muted-foreground">
                We'll send a confirmation link to your new email. The change takes effect after you confirm.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={changeEmail}
                  disabled={emailSaving}
                  className="bg-foreground text-background px-4 py-2 text-xs rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  {emailSaving ? "Sending..." : "Send confirmation"}
                </button>
                <button
                  onClick={() => { setEditEmail(false); setNewEmail(""); }}
                  className="border border-border px-4 py-2 text-xs rounded-md hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <input
              value={user?.email ?? ""}
              disabled
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-muted text-muted-foreground"
            />
          )}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-foreground text-background px-5 py-2.5 text-sm rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </section>
  );
}

function PlaceholderSection({ sectionKey }: { sectionKey: string }) {
  const meta = META[sectionKey];
  if (!meta) {
    return (
      <section className="bg-card rounded-md border border-border p-10 text-center">
        <p className="text-sm text-muted-foreground">Page not found.</p>
      </section>
    );
  }
  return (
    <section className="bg-card rounded-md border border-border p-10 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        {meta.icon}
      </div>
      <h1 className="mt-4 text-lg font-semibold">{meta.title}</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">{meta.empty}</p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-sm rounded-md hover:opacity-90"
      >
        Continue Shopping
      </Link>
    </section>
  );
}
